import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

import Input from "../../../components/form-item/Input";
import Toggle from "../../../components/form-item/Toggle";
import Stepper from "../../../components/stepper/Stepper";
import UserItem from "../../../components/user-item/UserItem";
import Modal, { type ModalAction } from "../../../components/modal/Modal";

import { useRegisterFromInvitationMutation } from "../../../app/services/invitations";
// @ts-expect-error - JSファイルの型定義なし
import { userApi } from "../../../app/accounts/authuser";
import {
  setAuthTokenToCookies,
  removeAuthTokenFromCookies,
} from "../../../common/utils";
import { extractErrorMessage } from "../inviteUtils";

// ===== Types =====

type RegistrationStep = "terms" | "password" | "profile";

interface InviteRegistrationModalProps {
  token: string;
  email: string;
  onComplete: (projectId: string, offerId?: string) => void;
  /** オファー招待のとき、送信者名。terms ステップにオファー文脈を表示する。 */
  senderName?: string;
}

// ===== Styled Components =====

const SectionTitle = styled.h2.attrs({
  className: "heading-spacing-1824",
})`
  margin-bottom: 8px;
  color: #000;
`;

const Description = styled.p.attrs({
  className: "bodytext u-line-height-150",
})`
  color: #000;
  white-space: pre-wrap;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const TermsContent = styled.div`
  margin: 24px 0;
  padding: 24px;
  background-color: var(--soremo-background);
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  max-height: 400px;
  overflow-y: auto;
`;

// ===== Component =====

// 登録ウィザードの順序とラベル。順序を変える際はここ一箇所だけ編集すればよいようにする。
const STEP_DEFINITIONS: Array<{ id: RegistrationStep; label: string }> = [
  { id: "terms", label: "利用許諾" },
  { id: "password", label: "パスワード" },
  { id: "profile", label: "プロフィール" },
];
const STEPS: RegistrationStep[] = STEP_DEFINITIONS.map((s) => s.id);
const STEP_LABELS: string[] = STEP_DEFINITIONS.map((s) => s.label);

const InviteRegistrationModal: React.FC<InviteRegistrationModalProps> = ({
  token,
  email,
  onComplete,
  senderName,
}) => {
  const dispatch = useDispatch();
  const [registerFromInvitation, { isLoading: isRegistering }] =
    useRegisterFromInvitationMutation();

  const [currentStep, setCurrentStep] = useState<RegistrationStep>("terms");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [registeredProjectId, setRegisteredProjectId] = useState<string>("");
  const [registeredOfferId, setRegisteredOfferId] = useState<string | undefined>(undefined);
  const [registeredUserId, setRegisteredUserId] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string>("");

  const currentStepIndex = STEPS.indexOf(currentStep);

  const handleNext = useCallback(() => {
    const idx = STEPS.indexOf(currentStep);
    if (idx < STEPS.length - 1) {
      setCurrentStep(STEPS[idx + 1]);
    }
  }, [currentStep]);

  const handleBack = useCallback(() => {
    const idx = STEPS.indexOf(currentStep);
    if (idx > 0) {
      setCurrentStep(STEPS[idx - 1]);
    }
  }, [currentStep]);

  const isPasswordValid = password.length >= 8 && password === passwordConfirm;

  // パスワードステップから呼ばれる: 招待経由の新規登録 API を叩き、新規ユーザーの
  // トークンで認証状態を差し替えてから、プロフィール設定ステップへ遷移する。
  const handleRegister = useCallback(async () => {
    setSubmitError("");

    try {
      // BE の DEFAULT_AUTHENTICATION_CLASSES で Token が Session より優先されるため
      // 旧セッションによる認証すり替えは構造的に起きないが、FE 側でも必ず auth_token を
      // 新しいトークンへ置換する。
      removeAuthTokenFromCookies();

      // BE 側で display_name が任意化されるまでの暫定として、メールのローカルパートを
      // デフォルト表示名として送る。
      const defaultDisplayName = email.split("@")[0] || "User";

      const formData = new FormData();
      formData.append("token", token);
      formData.append("password", password);
      formData.append("terms_accepted", "true");
      formData.append("display_name", defaultDisplayName);

      const result = await registerFromInvitation(formData).unwrap();

      if (!result.token) {
        throw new Error(
          "登録は完了しましたが、認証情報の取得に失敗しました。ページを再読み込みしてサインインしてください。"
        );
      }

      setAuthTokenToCookies(result.token);
      dispatch(userApi.util.resetApiState());
      setRegisteredProjectId(result.project_id);
      setRegisteredOfferId(result.offer_id);
      setRegisteredUserId(result.user_id);
      handleNext();
    } catch (err) {
      setSubmitError(
        extractErrorMessage(err, "登録に失敗しました。もう一度お試しください。")
      );
    }
  }, [token, password, email, registerFromInvitation, dispatch, handleNext]);

  // ===== ステップ描画 =====

  const renderStepContent = () => {
    switch (currentStep) {
      case "terms":
        return (
          <motion.div
            key="terms"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {senderName && (
              <div
                className="bodytext-13 u-text-light-gray"
                style={{ marginBottom: 8 }}
              >
                {senderName}さんからオファーが届いています
              </div>
            )}
            <SectionTitle>利用許諾</SectionTitle>
            <Description>
              サービスのご利用にあたり、以下の利用規約をお読みいただき、同意してください。
            </Description>

            <TermsContent>
              <div className="bodytext u-line-height-150">
                <p style={{ marginBottom: 16 }}>
                  <strong>SOREMO サービス利用規約</strong>
                </p>
                <p style={{ marginBottom: 12 }}>
                  本規約は、株式会社ソレモ（以下「当社」）が提供するサービス「SOREMO」（以下「本サービス」）の利用条件を定めるものです。
                </p>
                <p style={{ marginBottom: 12 }}>
                  第1条（適用）
                  <br />
                  本規約は、本サービスの利用に関する当社とユーザーとの間の権利義務関係を定めることを目的とし、ユーザーと当社との間の本サービスの利用に関わる一切の関係に適用されます。
                </p>
                <p style={{ marginBottom: 12 }}>
                  第2条（利用登録）
                  <br />
                  登録希望者が当社の定める方法によって利用登録を申請し、当社がこれを承認することによって、利用登録が完了するものとします。
                </p>
                <p style={{ marginBottom: 12 }}>
                  第3条（秘密保持）
                  <br />
                  ユーザーは、本サービスの利用にあたり知り得た情報について、当社の事前の書面による承諾がない限り、第三者に開示または漏洩してはならないものとします。
                </p>
                <p>
                  第4条（個人情報の取り扱い）
                  <br />
                  当社は、ユーザーの個人情報を当社のプライバシーポリシーに従い適切に取り扱います。
                </p>
              </div>
            </TermsContent>

            <Toggle
              value={termsAccepted}
              onClick={() => setTermsAccepted(!termsAccepted)}
            >
              <span className="bodytext">
                <a
                  href="/privacypolicy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  style={{
                    color: "var(--soremo-blue)",
                    textDecoration: "none",
                    fontSize: "inherit",
                  }}
                >
                  利用規約・個人情報の取り扱い
                </a>
                について同意します
              </span>
            </Toggle>
          </motion.div>
        );

      case "password":
        return (
          <motion.div
            key="password"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <SectionTitle>パスワード設定</SectionTitle>
            <Description>
              サインインに使用するパスワードを設定してください。
            </Description>

            <div className="bodytext-13 u-text-light-gray" style={{ marginTop: 8, marginBottom: 32 }}>
              メールアドレス: {email}
            </div>

            <FormGroup>
              <Input
                label="パスワード"
                option="required"
                type="password"
                placeholder="8文字以上のパスワード"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPassword(e.target.value)
                }
                error={password && password.length < 8 ? "パスワードは8文字以上で設定してください" : ""}
              />
            </FormGroup>

            <FormGroup>
              <Input
                label="パスワード（確認）"
                option="required"
                type="password"
                placeholder="もう一度入力してください"
                value={passwordConfirm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPasswordConfirm(e.target.value)
                }
                error={passwordConfirm && password !== passwordConfirm ? "パスワードが一致しません" : ""}
              />
            </FormGroup>

            {submitError && (
              <div
                className="bodytext-13"
                style={{
                  color: "var(--soremo-green)",
                  padding: "8px 12px",
                  borderRadius: 4,
                  marginTop: 16,
                }}
              >
                {submitError}
              </div>
            )}
          </motion.div>
        );

      case "profile":
        return (
          <motion.div
            key="profile"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <SectionTitle>プロフィール設定</SectionTitle>
            <Description>
              表示名やアバター画像を設定してください。{"\n"}
              この設定はマイページからいつでも変更できます。
            </Description>

            <div style={{ marginTop: 32 }}>
              <UserItem
                size={96}
                editable={true}
                hideBadges={true}
                expectedUserId={registeredUserId ?? undefined}
                expectedEmail={email}
              />
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  const getPrimaryAction = (): ModalAction => {
    switch (currentStep) {
      case "terms":
        return {
          label: "次へ",
          onClick: handleNext,
          disabled: !termsAccepted,
          rightIcon: "chevron_right",
        };
      case "password":
        return {
          label: "登録して次へ",
          onClick: handleRegister,
          disabled: !isPasswordValid,
          loading: isRegistering,
          rightIcon: isRegistering ? undefined : "chevron_right",
        };
      case "profile":
        return {
          label: "完了",
          onClick: () => onComplete(registeredProjectId, registeredOfferId),
          rightIcon: "chevron_right",
        };
    }
  };

  const getSecondaryAction = (): ModalAction | undefined => {
    if (currentStep === "password") {
      return {
        label: "戻る",
        onClick: handleBack,
        disabled: isRegistering,
      };
    }
    return undefined;
  };

  return (
    <Modal
      variant="fullscreen"
      headerExtras={
        <Stepper
          stepLabels={STEP_LABELS}
          current={currentStepIndex + 1}
          showCheckOnComplete={true}
        />
      }
      bodyWidth="reading"
      primaryAction={getPrimaryAction()}
      secondaryAction={getSecondaryAction()}
    >
      <AnimatePresence mode="wait">{renderStepContent()}</AnimatePresence>
    </Modal>
  );
};

export default InviteRegistrationModal;
